import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CreditCard, Check, ExternalLink, Loader2, Tag, X, Settings, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useT, useLang } from "@/lib/i18n";

// Per-cycle multiplier on the base monthly price (mirrors backend BILLING_CYCLES).
const CYCLES = {
  monthly:    { mult: 1,   off: null,  label: { pt: "Mensal", en: "Monthly" } },
  semiannual: { mult: 0.9, off: "-10%", label: { pt: "Semestral", en: "Semiannual" } },
  annual:     { mult: 0.8, off: "-20%", label: { pt: "Anual", en: "Annual" } },
};

function formatPrice(plan, lang) {
  if (plan.price_brl === null || plan.price_usd === null) return null;
  if (lang === "pt") return { sym: "R$ ", value: plan.price_brl?.toLocaleString("pt-BR") };
  return { sym: "$", value: plan.price_usd?.toLocaleString("en-US") };
}

export default function Billing() {
  const t = useT();
  const { lang } = useLang();
  const [data, setData] = useState({ plans: [] });
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setData(data));
    api.get("/orgs/current").then(({ data }) => setCurrent(data));
  }, []);

  const validateCode = async () => {
    const code = discountCode.trim();
    if (!code) return;
    setValidatingCode(true);
    try {
      const { data } = await api.post("/billing/validate-discount", { code });
      if (data.valid && data.percent) {
        setAppliedDiscount(data);
        toast.success(`${data.label[lang]} · -${data.percent}%`);
      } else {
        toast.error(lang === "pt" ? "Cupom inválido" : "Invalid code");
        setAppliedDiscount(null);
      }
    } catch { toast.error("Failed"); }
    finally { setValidatingCode(false); }
  };

  const clearDiscount = () => { setDiscountCode(""); setAppliedDiscount(null); };

  const upgradeStripe = async (planId) => {
    setLoading(planId + "-stripe");
    try {
      const origin = window.location.origin + "/app";
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: origin,
        currency: lang === "pt" ? "brl" : "usd",
        billing_cycle: cycle,
        discount_code: appliedDiscount?.code || null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setLoading(null);
    }
  };

  const requestPaypal = async (planId) => {
    // PayPal coming soon — not yet enabled.
    toast.info("PayPal em breve.");
  };

  const openPortal = async () => {
    setLoading("portal");
    try {
      const origin = window.location.origin + "/app/billing";
      const { data } = await api.post("/billing/portal-session", { return_url: origin });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.info(data.message || (lang === "pt" ? "Portal indisponível no momento." : "Portal unavailable right now."));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(null);
    }
  };

  const cancelSub = async () => {
    const ok = window.confirm(lang === "pt"
      ? "Cancelar a assinatura? Você mantém o acesso até o fim do período já pago — não há reembolso proporcional do tempo restante."
      : "Cancel the subscription? You keep access until the end of the already-paid period — there is no proportional refund of the remaining time.");
    if (!ok) return;
    setLoading("cancel");
    try {
      await api.post("/billing/cancel");
      toast.success(lang === "pt" ? "Assinatura será encerrada no fim do período pago." : "Subscription will end at the paid period's end.");
      const { data } = await api.get("/orgs/current");
      setCurrent(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(null);
    }
  };

  const resumeSub = async () => {
    setLoading("resume");
    try {
      await api.post("/billing/resume");
      toast.success(lang === "pt" ? "Assinatura reativada." : "Subscription resumed.");
      const { data } = await api.get("/orgs/current");
      setCurrent(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(null);
    }
  };

  if (!current) return null;
  const org = current.org;
  const currentPlanId = org.plan;
  const cancelScheduled = !!org.subscription_cancel_at_period_end;
  const periodEnd = org.subscription_current_period_end
    ? new Date(org.subscription_current_period_end * 1000).toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US")
    : null;
  const discountMultiplier = appliedDiscount ? (1 - appliedDiscount.percent / 100) : 1;

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="overline mb-3">{t("billing.overline")}</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mb-3">{t("billing.title")}</h1>
      <p className="text-white/50 mb-6 text-sm">{t("billing.currentlyOn")} <span className="text-primary font-mono">{currentPlanId.toUpperCase()}</span>.</p>

      {currentPlanId !== "trial" && (
        <div className="mb-6 space-y-3">
          {cancelScheduled ? (
            <div className="surface border-amber-500/30 bg-amber-500/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="billing-cancel-notice">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
              <div className="text-xs text-white/70 flex-1">
                {lang === "pt"
                  ? <>Assinatura cancelada. Você mantém acesso ao plano {periodEnd ? <>até <span className="font-mono text-white">{periodEnd}</span></> : "até o fim do período pago"} e não será cobrado de novo. Sem reembolso do período já pago.</>
                  : <>Subscription canceled. You keep access {periodEnd ? <>until <span className="font-mono text-white">{periodEnd}</span></> : "until the paid period ends"} and won't be charged again. No refund for the paid period.</>}
              </div>
              <button onClick={resumeSub} disabled={loading === "resume"} className="btn-ghost text-xs py-2 px-3" data-testid="billing-resume-btn">
                {loading === "resume" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {lang === "pt" ? "Reativar" : "Resume"}
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button onClick={cancelSub} disabled={loading === "cancel"} className="btn-ghost text-xs py-2 px-3" data-testid="billing-cancel-btn">
                {loading === "cancel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                {lang === "pt" ? "Cancelar assinatura" : "Cancel subscription"}
              </button>
              <button onClick={openPortal} disabled={loading === "portal"} className="btn-ghost text-xs py-2 px-3" data-testid="billing-portal-btn">
                {loading === "portal" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Settings className="w-3.5 h-3.5" />}
                {lang === "pt" ? "Fatura / dados de pagamento" : "Invoice / payment details"}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 mb-10">
        <div className="inline-flex items-center border border-white/10 font-mono text-xs">
          {["monthly", "semiannual", "annual"].map((c) => (
            <button key={c} onClick={() => setCycle(c)} className={`px-4 py-2 ${cycle === c ? "bg-white/10" : "text-white/50"}`}
              data-testid={`billing-cycle-${c}`}>
              {CYCLES[c].label[lang === "pt" ? "pt" : "en"]}
              {CYCLES[c].off && <span className="text-primary ml-1">{CYCLES[c].off}</span>}
            </button>
          ))}
        </div>

        <div className="inline-flex items-center gap-2" data-testid="billing-discount-block">
          <Tag className="w-4 h-4 text-white/40" />
          {appliedDiscount ? (
            <div className="inline-flex items-center gap-2 border border-primary/40 bg-primary/10 px-3 py-2 font-mono text-xs">
              <span className="text-primary uppercase">{appliedDiscount.code}</span>
              <span className="text-white/80">-{appliedDiscount.percent}%</span>
              <button onClick={clearDiscount} className="text-white/40 hover:text-white" data-testid="billing-discount-clear">
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <>
              <input
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                placeholder={lang === "pt" ? "Código de cupom" : "Discount code"}
                className="bg-white/5 border border-white/10 px-3 py-2 text-xs font-mono w-44 focus:outline-none focus:border-primary"
                data-testid="billing-discount-input"
              />
              <button onClick={validateCode} disabled={validatingCode || !discountCode.trim()}
                className="btn-ghost text-xs py-2 px-3"
                data-testid="billing-discount-apply">
                {validatingCode ? <Loader2 className="w-3 h-3 animate-spin" /> : (lang === "pt" ? "Aplicar" : "Apply")}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        {data.plans.map((plan) => {
          const isCurrent = plan.id === currentPlanId;
          const p = formatPrice(plan, lang);
          let displayValue = null;
          if (p && plan.price_brl) {
            let base = lang === "pt" ? plan.price_brl : plan.price_usd;
            base = base * CYCLES[cycle].mult;
            base = base * discountMultiplier;
            displayValue = base.toLocaleString(lang === "pt" ? "pt-BR" : "en-US", {maximumFractionDigits: 0});
          }
          return (
            <div key={plan.id} className={`surface p-6 ${plan.id === "professional" ? "border-primary" : ""}`} data-testid={`billing-plan-${plan.id}`}>
              <div className="overline mb-1">{plan.id}</div>
              <div className="font-display font-bold text-2xl">{plan.name}</div>
              <p className="text-xs text-white/40 font-mono mt-1 mb-3">{plan.tagline?.[lang]}</p>
              <div className="mt-2 mb-4">
                {p === null ? <div className="font-mono text-2xl">Custom</div>
                  : p.value === "0" ? <span className="font-mono text-2xl">{lang === "pt" ? "Grátis" : "Free"}</span>
                  : (
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="font-mono text-3xl font-bold">{p.sym}{displayValue}</span>
                        <span className="text-white/40 text-sm">/{lang === "pt" ? "mês" : "mo"}</span>
                      </div>
                      {(cycle !== "monthly" || appliedDiscount) && (
                        <div className="text-[10px] text-white/40 font-mono mt-1">
                          <span className="line-through">{p.sym}{p.value}</span>
                          {cycle !== "monthly" && <span className="text-primary ml-1">{CYCLES[cycle].label[lang === "pt" ? "pt" : "en"].toLowerCase()}</span>}
                          {appliedDiscount && <span className="text-primary ml-1">-{appliedDiscount.percent}%</span>}
                        </div>
                      )}
                    </div>
                  )}
              </div>
              <div className="text-xs font-mono text-white/50 space-y-1 mb-5">
                <div>Cards: {plan.cards_limit === -1 ? "∞" : `${plan.cards_limit}/${lang === "pt" ? "mês" : "mo"}`}</div>
                <div>{lang === "pt" ? "Membros" : "Members"}: {plan.members_limit === -1 ? "∞" : plan.members_limit}</div>
                <div>API: {plan.api_access ? "✓" : "—"}</div>
              </div>
              {isCurrent ? (
                <div className="inline-flex items-center gap-1.5 text-xs text-emerald-400 font-mono">
                  <Check className="w-3.5 h-3.5" /> {t("billing.active")}
                </div>
              ) : plan.id === "enterprise" ? (
                <a href="mailto:sales@ratchet.dev" className="btn-ghost w-full text-xs py-2" data-testid={`billing-cta-${plan.id}`}>
                  {t("pricing.contactSales")} <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : plan.id === "trial" ? (
                <div className="text-xs text-white/40 font-mono">{t("billing.startingTier")}</div>
              ) : (
                <div className="space-y-2">
                  <button onClick={() => upgradeStripe(plan.id)} disabled={!!loading} className="btn-primary w-full text-xs py-2"
                    data-testid={`billing-stripe-${plan.id}`}>
                    {loading === plan.id + "-stripe" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CreditCard className="w-3.5 h-3.5" /> {t("billing.payCard")}</>}
                  </button>
                  <div className="flex items-center gap-1.5 justify-center text-[10px] font-mono text-white/40" data-testid={`billing-methods-${plan.id}`}>
                    <CreditCard className="w-3 h-3" />
                    <span>{lang === "pt" ? "Cartão · renovação automática" : "Card · auto-renewing"}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="surface p-6">
        <div className="overline mb-3">{t("billing.notes")}</div>
        <ul className="text-sm text-white/60 space-y-1.5">
          <li>• {lang === "pt"
            ? "Assinatura recorrente por cartão (mensal, semestral ou anual). Renova automaticamente ao fim de cada ciclo."
            : "Recurring card subscription (monthly, semiannual or annual). Auto-renews at the end of each cycle."}</li>
          <li>• {lang === "pt"
            ? "Cancele quando quiser: o acesso continua até o fim do período já pago, sem reembolso proporcional do tempo restante."
            : "Cancel anytime: access continues until the end of the paid period, with no proportional refund of the remaining time."}</li>
          <li>• {t("billing.note3")}</li>
          <li>• {lang === "pt" ? "Cupons aceitos: STARTUP, EARLY_ADOPTER, REFERRAL" : "Codes accepted: STARTUP, EARLY_ADOPTER, REFERRAL"}</li>
          <li>• <Link to="/terms" className="text-primary hover:underline">{lang === "pt" ? "Termos de Uso e política de cobrança" : "Terms of Use and billing policy"}</Link></li>
        </ul>
      </div>
    </div>
  );
}
